import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Vehicle, VehicleGroup } from '@fc-core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { TooltipComponent } from '@fc-shared/ui/tooltips/tooltip.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DvrSettings } from '@fc-store/model/dvr-settings';
import { CoreGroups } from '@fc-vehicles/components/vehicle-group-colors/core.groups';
import { HasPermissionDirective } from '@fc-shared/directives/has-permission.directive';
import { StatusTagComponent } from '@fc-shared/ui/status-tag/status-tag.component';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { Checkbox } from '@fc-shared/ui/checkbox/checkbox';
import { EmptyStateComponent } from '@fc-shared/ui/empty-state/empty-state.component';
import { SkeletonModule } from '@fc-shared/ui/skeleton/skeleton.module';
import { VehicleSmallItemComponent } from '../vehicle-small-item/vehicle-small-item.component';
import { TableDvrSettings } from '../../models/dvr-settings.model';

@Component({
  selector: 'fc-dvr-settings-table',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    TooltipComponent,
    MatProgressSpinnerModule,
    HasPermissionDirective,
    StatusTagComponent,
    IconComponent,
    ButtonComponent,
    Checkbox,
    EmptyStateComponent,
    SkeletonModule,
    VehicleSmallItemComponent,
  ],
  templateUrl: './dvr-settings-table.component.html',
  host: { class: 'flex flex-col overflow-auto' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DvrSettingsTableComponent {
  updatingCameraIds = input<number[]>();
  dvrSettings = input<DvrSettings[]>([]);
  settingsLoading = input<boolean>();
  dvrTableData = input<TableDvrSettings[]>();
  vehicleGroups = input<VehicleGroup[]>();

  selectedVehicles = output<Vehicle[]>();
  editDvrSettings = output<Vehicle[]>();

  paginator = viewChild(MatPaginator);

  tableColumns =
    '48px minmax(180px, 2fr) minmax(110px, 1fr) minmax(130px, 1fr) minmax(80px, 0.7fr) minmax(120px, 1fr) minmax(160px, 1.5fr)';
  readonly skeletonRows = Array.from({ length: 10 });
  dataSource = new MatTableDataSource<TableDvrSettings>();
  pagedData = toSignal(this.dataSource.connect(), {
    initialValue: [] as TableDvrSettings[],
  });

  constructor() {
    effect(() => {
      this.dataSource.data = this.dvrTableData() ?? [];
      this.dataSource.paginator = this.paginator();
    });
  }

  toggleAll() {
    if (!this.dvrSettings()?.length) return;
    const filteredList = this.dataSource.data.filter(
      (settings) =>
        !this.dvrSettings()?.find(
          (setting) => setting.vehicle?.id === settings.vehicle.id,
        )?.isPending,
    );
    const enabled = filteredList.every((item) => item.vehicle.enabled);
    filteredList.forEach((item) => {
      if (!item.isPending) item.vehicle.enabled = !enabled;
    });
    this.selectedVehicles.emit(this.enabledVehicles);
  }

  getVehicleGroups(vehicle: Vehicle): CoreGroups[] {
    if (!this.vehicleGroups()?.length) return [];
    return (this.vehicleGroups() ?? []).filter((group) =>
      group.vehicles?.includes(vehicle.id),
    ) as CoreGroups[];
  }

  get noData(): boolean {
    return this.dataSource.data.length === 0;
  }

  get enabledVehicles(): Vehicle[] {
    return this.dataSource.data
      .filter((item) => item.vehicle.enabled)
      .map((item) => item.vehicle);
  }

  get allSelected(): boolean {
    if (!this.dataSource.data.length) return false;
    return this.dataSource.data.every((item) => item.vehicle.enabled);
  }

  get someSelected(): boolean {
    return this.dataSource.data.some((item) => item.vehicle.enabled);
  }

  toggleItem(item: TableDvrSettings) {
    item.vehicle = {
      ...item.vehicle,
      enabled: !item.vehicle.enabled,
    };
    this.selectedVehicles.emit(this.enabledVehicles);
  }

  openEditDvrSettings(data: TableDvrSettings) {
    this.editDvrSettings.emit([data.vehicle]);
  }
}

export function filterVehiclesByGroup(
  vehicle: Vehicle,
  selectedGroups: VehicleGroup[],
): boolean {
  if (!selectedGroups?.length) return true;
  const selectedGroupIds = selectedGroups
    .map((group) => group.id)
    .filter((id): id is number => id != null);
  const vehicleGroupIds = vehicle.groups;
  return selectedGroupIds.every((id) => vehicleGroupIds.includes(id));
}
